"use client";
import { motion } from "framer-motion";
import {
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiArrowUp,
  FiMail,
} from "react-icons/fi";
import Link from "next/link";
import { useLanguage } from "@/context/LanguageContext";

export const socialLinks = [
  { icon: <FiGithub aria-hidden="true" />, href: "https://github.com/emmanuelAlexis", label: "GitHub" },
  { icon: <FiLinkedin aria-hidden="true" />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FiMail aria-hidden="true" />, href: "mailto:emmanueladolphe0401@gmail.com", label: "Email" },
];

export default function Footer() {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const quickLinks = [
    { name: t.nav.about, href: "/#about" },
    { name: t.nav.projects, href: "/#projects" },
    { name: t.nav.contact, href: "/#contact" },
  ];

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="section-atmosphere relative overflow-hidden border-t border-foreground/10 pt-20 pb-8"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-[0.02] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand Column */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <Link href="/" className="text-2xl font-bold block">
              <span className="text-primary">emmanuel</span>
              <span className="text-gray-500 dark:text-gray-400">.A.A</span>
            </Link>
            <p className="text-muted-foreground max-w-xs leading-relaxed">
              {t.hero.role} React & Next.js. {t.hero.description}
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
              {t.footer.quickLinks}
            </h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/50 group-hover:bg-primary transition-colors" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">
              {t.footer.socials}
            </h2>
            <div className="flex flex-wrap gap-4">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="size-10 rounded-full border border-foreground/10 bg-background/45 flex items-center justify-center text-muted-foreground hover:border-primary/50 hover:bg-primary hover:text-white transition-all shadow-sm hover:shadow-lg hover:shadow-primary/20 backdrop-blur-md"
                  aria-label={social.label}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-gray-500 dark:text-gray-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} Emmanuel A.A. {t.footer.copyright}.
          </p>

          <p className="text-gray-500 dark:text-gray-500 text-sm flex items-center gap-1">
            {t.footer.designedBy}
          </p>
        </motion.div>
      </div>

      {/* Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -3 }}
        className="absolute bottom-8 right-8 size-11 rounded-full border border-[#a85f38]/70 bg-background/70 text-[#a85f38] shadow-lg shadow-[#a85f38]/15 backdrop-blur-md flex items-center justify-center hover:bg-primary hover:text-white transition-all z-20"
        aria-label="Scroll to top"
      >
        <FiArrowUp aria-hidden="true" />
      </motion.button>
    </motion.footer>
  );
}
